import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getWaitingConfirmServices } from '@/modules/Service/services'
import {
    createServiceColumns,
    createServiceFilters,
    SERVICE_FILTER_DEFAULTS,
    SERVICE_DEFAULT_SORT,
    WAITING_CONFIRM_STATUSES,
    type ServiceFilters,
} from '@/modules/Service/utils/service-list-config'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import type { Service } from '@/modules/Service/types'

/**
 * Business logic hook for Waiting Confirm Service List
 * Includes additional column for commission tracking
 */
export const useWaitingConfirmList = () => {
    const { t } = useTranslation('services')

    // URL params management
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ServiceFilters>({
        defaults: {
            sort_by: SERVICE_DEFAULT_SORT.sort_by,
            sort_order: SERVICE_DEFAULT_SORT.sort_order,
            page_size: 20,
        },
        filterDefaults: SERVICE_FILTER_DEFAULTS,
    })

    // Form for filter controls
    const { control } = useForm<ServiceFilters>({
        defaultValues: filters,
    })

    // Fetch services data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_WAITING, queryParams],
        queryFn: () => getWaitingConfirmServices(queryParams),
        placeholderData: true,
    })

    // Base columns
    const baseColumns = useMemo(() => createServiceColumns(t), [t])

    // Add commission tracking column
    const columns = useMemo<DataTableColumn<Service>[]>(() => {
        // Filter out columns not needed for waiting confirm
        const filteredColumns = baseColumns.filter(
            (col) =>
                !['name', 'service_stopped_date'].includes(col.key as string)
        )

        // Add commission tracking column
        return [
            ...filteredColumns,
            {
                key: 'employee_sale_tracking_ids',
                title: t('services.commissions'),
                width: 200,
                render: (_, row) => {
                    const hasTracking =
                        row?.employee_sale_tracking_ids &&
                        row?.employee_sale_tracking_ids.length > 0
                    return (
                        <div
                            className={`px-2 py-1 rounded text-xs inline-block ${
                                hasTracking
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                            }`}
                        >
                            {hasTracking
                                ? t('commissions.assigned')
                                : t('commissions.not-assigned')}
                        </div>
                    )
                },
            },
        ]
    }, [baseColumns, t])

    const filterItems = useMemo(
        () =>
            createServiceFilters(t, {
                serviceStatuses: WAITING_CONFIRM_STATUSES,
                showAccountManager: false,
                showDateFilters: true,
            }),
        [t]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        services: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}

export default useWaitingConfirmList
